import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  type User,
} from "firebase/auth";
import { auth, googleProvider, dominioPermitido, DOMINIOS_PERMITIDOS } from "@/lib/firebase";
import { aposLogin } from "@/lib/fns";
import { mensagemDeErro, ehDesistencia, ehFalhaDeConfiguracao } from "@/lib/erros";
import type { Papel } from "@/lib/tipos";

/**
 * Estados possíveis da sessão.
 *
 * "negado" é diferente de "deslogado": a pessoa entrou no Google com sucesso,
 * mas a conta não é do Workspace do grupo ou ainda não recebeu papel. Mostrar
 * a tela de login de novo nesse caso só faria ela tentar para sempre.
 */
export type EstadoSessao = "carregando" | "deslogado" | "liberando" | "liberado" | "negado";

interface Sessao {
  estado: EstadoSessao;
  usuario: User | null;
  papeis: Papel[];
  /** Empresas a que a pessoa está limitada. Vazio é o grupo inteiro (§13). */
  empresas: string[];
  erro: string | null;
  /**
   * A falha é de configuração do projeto, não da pessoa — provedor desabilitado,
   * domínio não autorizado. Tentar de novo não resolve, então a tela troca o tom.
   */
  erroDeConfiguracao: boolean;
  entrar: () => Promise<void>;
  sair: () => Promise<void>;
  podeEscrever: boolean;
  podeAprovar: boolean;
  isAdmin: boolean;
}

const Ctx = createContext<Sessao | null>(null);

export function ProvedorSessao({ children }: { children: ReactNode }) {
  const [estado, setEstado] = useState<EstadoSessao>("carregando");
  const [usuario, setUsuario] = useState<User | null>(null);
  const [papeis, setPapeis] = useState<Papel[]>([]);
  const [empresas, setEmpresas] = useState<string[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [erroDeConfiguracao, setErroDeConfiguracao] = useState(false);

  // Evita chamar aposLogin em cada re-render do mesmo usuário.
  const liberado = useRef<string | null>(null);

  useEffect(() => {
    return onAuthStateChanged(auth, async (u) => {
      setUsuario(u);

      if (!u) {
        liberado.current = null;
        setPapeis([]);
        setEmpresas([]);
        setErro(null);
        setErroDeConfiguracao(false);
        setEstado("deslogado");
        return;
      }

      // Barreira amigável: o servidor também recusa, mas aqui a pessoa descobre
      // na hora, sem esperar uma chamada que vai falhar de qualquer jeito.
      if (!dominioPermitido(u.email)) {
        setPapeis([]);
        setErro(
          `A conta ${u.email ?? ""} não é do Workspace da Lonepine (${DOMINIOS_PERMITIDOS.join(", ")}).`,
        );
        setEstado("negado");
        return;
      }

      try {
        setEstado((atual) => (atual === "liberado" ? atual : "liberando"));

        let token = await u.getIdTokenResult();
        let atuais = (token.claims.papeis as Papel[] | undefined) ?? [];
        let recorte = (token.claims.empresas as string[] | undefined) ?? [];

        if (liberado.current !== u.uid || atuais.length === 0) {
          // Cria o perfil no primeiro acesso e devolve o papel; o claim só
          // aparece no token depois de um refresh forçado.
          const r = await aposLogin({});
          liberado.current = u.uid;
          token = await u.getIdTokenResult(true);
          atuais = ((token.claims.papeis as Papel[] | undefined) ?? (r.papeis as Papel[])) ?? [];
          recorte = (token.claims.empresas as string[] | undefined) ?? [];
        }

        setPapeis(atuais);
        setEmpresas(recorte);
        setErro(null);
        setEstado(atuais.length > 0 ? "liberado" : "negado");
      } catch (e) {
        setPapeis([]);
        setErro(mensagemDeErro(e));
        setEstado("negado");
      }
    });
  }, []);

  const entrar = useCallback(async () => {
    setErro(null);
    setErroDeConfiguracao(false);
    try {
      await signInWithPopup(auth, googleProvider);
      // O resto acontece em onAuthStateChanged.
    } catch (e) {
      // Fechar a janela do Google é desistência, não erro para mostrar na tela.
      if (ehDesistencia(e)) return;
      setErro(mensagemDeErro(e));
      setErroDeConfiguracao(ehFalhaDeConfiguracao(e));
    }
  }, []);

  const sair = useCallback(async () => {
    liberado.current = null;
    await signOut(auth);
  }, []);

  const valor = useMemo<Sessao>(
    () => ({
      estado,
      usuario,
      papeis,
      empresas,
      erro,
      erroDeConfiguracao,
      entrar,
      sair,
      podeEscrever: papeis.some((p) => p === "admin" || p === "lancador" || p === "aprovador"),
      podeAprovar: papeis.some((p) => p === "admin" || p === "aprovador"),
      isAdmin: papeis.includes("admin"),
    }),
    [estado, usuario, papeis, empresas, erro, erroDeConfiguracao, entrar, sair],
  );

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useSessao(): Sessao {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useSessao precisa estar dentro de <ProvedorSessao>.");
  return ctx;
}
